import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';

import { Form, Input, Row, Col, Radio, Typography, Button, Flex, Tag, Select } from 'antd';
import { checkPermissions } from '../../../utils/permissions';
import { appConfigAtomState } from '../../../state/reducerAtoms/app.atom';
import { connectSocket, joinTicketRoom, leaveTicketRoom, joinUniversityRoom } from '../../../services/socket';

import { translateThis } from '../../../helpers/language.helper';
import AppIcons from '../../../assets/images/icons';
import TypingEffect from '../../../components/texts/typingEffect';
import { showSnackbar } from '../../../components/snackbar/index';
import ApiCaller from '../../../services/api/general';
import LogoPreview from '../../../components/logoPreview/logoPreview';
import './index.scss';
import PageLayout from '../../app';
import AvatarViewer from '../../../components/iconPicker/avatarViewer';
import SVGIcons from '../../../assets/images/icons/svgIcons';
import { DATE_FORMAT_YEAR_TIME_SHORT } from '../../../constants';

const { Title, Paragraph } = Typography;

const getDateLabel = (date) => {
    const d = dayjs(date);
    const diffDays = dayjs().startOf('day').diff(d.startOf('day'), 'day');
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.format('dddd');
    return d.format('MMMM D, YYYY');
};


export default function ViewServiceTicket() {
    const appState = useRecoilValue(appConfigAtomState);
    const profile = appState?.profile || {};

    const { id } = useParams();
    const location = useLocation();
    const isHumanChatUrl = new URLSearchParams(location.search).get('type') === 'human';

    const [isLoading, setIsLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [aiTicketContextId, setAiTicketContextId] = useState('');
    const [aiTicketModuleId, setAiTicketModuleId] = useState('');
    const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);

    // Human chat state
    const [showHumanPanel, setShowHumanPanel] = useState(isHumanChatUrl);
    const [humanTicketId, setHumanTicketId] = useState(isHumanChatUrl ? id : '');
    const [humanTicket, setHumanTicket] = useState(null);
    const [aiHistory, setAiHistory] = useState([]);
    const [liveMessages, setLiveMessages] = useState([]);
    const [agentInput, setAgentInput] = useState('');
    const [chatStatus, setChatStatus] = useState(null); // pending | active | completed | timed_out
    const liveChatRef = useRef(null);

    // Live human-support request linked to THIS AI conversation (via contextId)
    const [humanRequestStatus, setHumanRequestStatus] = useState(null); // null | 'pending' | 'active'
    const [humanRequestTicketId, setHumanRequestTicketId] = useState(null);

    const [formData, setFormData] = useState({
        ticketNumber: '',
        userName: '',
        email: '',
        phoneNumber: '',
        chatTitle: '',
        chatSummary: '',
        moduleTitle: '',
        moduleDescription: '',
        topicStatus: '',
        avatarImg: '',
        agentType: 'customer_support',
        createdAt: '',
    });

    const [salesData, setSalesData] = useState({
        interestIn: '',
        goal: '',
        timelineUrgency: '',
        preferredContact: '',
        meetingDateTime: '',
        contactDetail: '',
        qualificationStatus: '',
        stage: 'New',
    });

    const [chatNotes, setChatNotes] = useState('');
    const [moduleTheme, setModuleTheme] = useState({
        themeBackgroundColor: '',
        chatHeaderImg: '',
        isHeaderPresent: false,
        conversationBoxTitle: '',
        chatLogoImg: '',
        agentName: '',
        isAddRotatingAvatarAndLogo: false,
    });

    useEffect(() => {
        if (!id) return;
        if (isHumanChatUrl) {
            setHumanTicketId(id);
            setShowHumanPanel(true);
            fetchHumanTicket(id);
        } else {
            getData(id);
        }
        // eslint-disable-next-line
    }, [id]);

    // Fetch human chat ticket data.
    // Pass preserveTheme=true when switching from an AI ticket (theme already loaded for same module).
    const fetchHumanTicket = async (ticketId, preserveTheme = false) => {
        try {
            const apiCaller = new ApiCaller(`inbox/human-chat`);
            const result = await apiCaller.getData(ticketId);
            if (result) {
                const ticket = result?.data ?? result;
                setHumanTicket(ticket);
                setAiHistory(ticket.aiConversation || []);
                setLiveMessages(ticket.agentMessages || []);
                setChatStatus(ticket.status);
                setFormData((prev) => ({
                    ticketNumber: '',
                    userName: ticket.name || '',
                    email: ticket.email || '',
                    phoneNumber: ticket.phoneNumber || '',
                    chatTitle: '',
                    chatSummary: '',
                    moduleTitle: ticket.moduleId?.title || '',
                    moduleDescription: '',
                    topicStatus: ticket.status || '',
                    avatarImg: ticket.moduleId?.avatarImg || (preserveTheme ? prev.avatarImg : ''),
                    agentType: 'customer_support',
                    createdAt: ticket.requestedAt || ticket.createdAt || '',
                }));
                if (!preserveTheme) {
                    setModuleTheme({
                        themeBackgroundColor: ticket.moduleId?.themeBackgroundColor || '',
                        chatHeaderImg: ticket.moduleId?.chatHeaderImg || '',
                        isHeaderPresent: ticket.moduleId?.isHeaderPresent || false,
                        conversationBoxTitle: ticket.moduleId?.conversationBoxTitle || '',
                        chatLogoImg: ticket.moduleId?.chatLogoImg || '',
                        agentName: ticket.moduleId?.agentName || '',
                        isAddRotatingAvatarAndLogo: ticket.moduleId?.isAddRotatingAvatarAndLogo || false,
                    });
                }
            }
        } catch (err) { console.error('fetchHumanTicket error:', err); } finally {
            setIsLoading(false);
        }
    };

    // Socket setup for human chat ticket
    useEffect(() => {
        if (!showHumanPanel || !humanTicketId) return;
        const socket = connectSocket();
        joinTicketRoom(humanTicketId);

        const onUserMessage = (msg) => setLiveMessages((prev) => [...prev, msg]);
        const onAgentMessage = (msg) => setLiveMessages((prev) => {
            const idx = prev.findIndex(m => m._optimistic && m.message === msg.message);
            if (idx !== -1) {
                const updated = [...prev];
                updated[idx] = msg;
                return updated;
            }
            return [...prev, msg];
        });
        const onChatCompleted = () => setChatStatus('completed');
        const onTimeout = () => setChatStatus('timed_out');

        socket.on('user_message', onUserMessage);
        socket.on('agent_message', onAgentMessage);
        socket.on('chat_completed', onChatCompleted);
        socket.on('agent_timeout', onTimeout);

        // eslint-disable-next-line consistent-return
        return () => {
            leaveTicketRoom(humanTicketId);
            socket.off('user_message', onUserMessage);
            socket.off('agent_message', onAgentMessage);
            socket.off('chat_completed', onChatCompleted);
            socket.off('agent_timeout', onTimeout);
        };
    }, [humanTicketId, showHumanPanel]);

    // Check for a live human-support request linked to this AI conversation (by contextId).
    // Re-runs whenever the contextId is known and listens to the university socket room so
    // the banner appears / disappears in real-time without a page refresh.
    useEffect(() => {
        if (!aiTicketContextId) return () => {};

        const checkHumanRequest = async () => {
            try {
                const apiCaller = new ApiCaller('inbox/human-chat/list');
                const result = await apiCaller.getCustomList('', { query: 'statuses=pending,active' });
                if (Array.isArray(result)) {
                    const match = result.find(r => r.contextId === aiTicketContextId);
                    if (match) {
                        setHumanRequestStatus(match.status);
                        setHumanRequestTicketId(match._id || match.id || null);
                    } else {
                        setHumanRequestStatus(null);
                        setHumanRequestTicketId(null);
                    }
                }
            } catch { /* ignore */ }
        };

        checkHumanRequest();

        const socket = connectSocket();
        const universityId = profile?.universityId;
        if (universityId) joinUniversityRoom(universityId);

        // Re-check from API on any request change so we always show accurate status
        socket.on('new_agent_request', checkHumanRequest);
        socket.on('ticket_updated', checkHumanRequest);

        return () => {
            socket.off('new_agent_request', checkHumanRequest);
            socket.off('ticket_updated', checkHumanRequest);
        };
    }, [aiTicketContextId, profile?.universityId]);

    // Scroll unified chat to bottom on any new message
    useEffect(() => {
        if (liveChatRef.current) {
            liveChatRef.current.scrollTop = liveChatRef.current.scrollHeight;
        }
    }, [aiHistory, liveMessages]);

    const acceptChat = async () => {
        try {
            const apiCaller = new ApiCaller(`inbox/${humanTicketId}/agent-connect`);
            const result = await apiCaller.postData({});
            if (result?.success) {
                setChatStatus('active');
                setHumanTicket((prev) => ({ ...prev, status: 'active' }));
            }
        } catch { /* ignore */ }
    };

    const sendAgentMessage = async () => {
        if (!agentInput.trim()) return;
        const msg = agentInput;
        setAgentInput('');
        setLiveMessages((prev) => [...prev, { sender: 'agent', message: msg, _optimistic: true }]);
        try {
            const apiCaller = new ApiCaller(`inbox/${humanTicketId}/agent-message`);
            await apiCaller.postData({ message: msg });
        } catch { /* ignore */ }
    };

    const endChat = async () => {
        try {
            const apiCaller = new ApiCaller(`inbox/${humanTicketId}/complete`);
            await apiCaller.postData({});
            setChatStatus('completed');
        } catch { /* ignore */ }
    };

    const restartChat = async () => {
        if (!aiTicketContextId) {
            showSnackbar().failure('No session found for this ticket');
            return;
        }
        try {
            setIsInitiatingChat(true);
            const apiCaller = new ApiCaller('inbox/agent-initiate');
            const result = await apiCaller.postData({
                contextId: aiTicketContextId,
                moduleId: aiTicketModuleId || null,
                userName: formData.userName || 'Guest',
                email: formData.email || '',
            });
            if (result?.success && result?.data?.ticketId) {
                const newTicketId = String(result.data.ticketId);
                setHumanTicketId(newTicketId);
                setChatStatus('active');
                await fetchHumanTicket(newTicketId, true);
            } else {
                showSnackbar().failure(result?.message || 'Could not restart chat');
            }
        } catch {
            showSnackbar().failure('Could not restart chat');
        } finally {
            setIsInitiatingChat(false);
        }
    };

    // get data from api
    const getData = (id) => {
        setIsLoading(true);
        const apiCaller = new ApiCaller('modules/conversation');
        apiCaller.getData(id)
            .then(({ data }) => {
                const formData = {
                    ticketNumber: data?.ticketNumber ?? '',
                    userName: data?.userName ?? '',
                    email: data?.userId ?? '',
                    phoneNumber: data?.phoneNumber ?? '',
                    moduleTitle: data?.moduleId?.title ?? '',
                    moduleDescription: data?.moduleId?.description ?? '',
                    chatTitle: data?.summaryTitle ?? '',
                    chatSummary: data?.chatSummary ?? '',
                    topicStatus: data?.topicStatus ?? '',
                    avatarImg: data?.moduleId.avatarImg ?? '',
                    agentType: data?.moduleId?.agentType ?? 'customer_support',
                    createdAt: data?.createdAt ?? 'createdAt',
                }
                setFormData(formData);
                if (data?.salesData) {
                    setSalesData({
                        interestIn: data.salesData.interestIn || '',
                        goal: data.salesData.goal || '',
                        timelineUrgency: data.salesData.timelineUrgency || '',
                        preferredContact: data.salesData.preferredContact || '',
                        meetingDateTime: data.salesData.meetingDateTime || '',
                        contactDetail: data.salesData.contactDetail || '',
                        qualificationStatus: data.salesData.qualificationStatus || '',
                        stage: data.salesData.stage || 'New',
                    });
                }
                setQuestions(data?.questions || []);
                setAiTicketContextId(data?.contextId || '');
                setAiTicketModuleId(data?.moduleId?._id || '');
                setModuleTheme({
                    themeBackgroundColor: data?.moduleId?.themeBackgroundColor || '',
                    chatHeaderImg: data?.moduleId?.chatHeaderImg || '',
                    isHeaderPresent: data?.moduleId?.isHeaderPresent || false,
                    conversationBoxTitle: data?.moduleId?.conversationBoxTitle || '',
                    chatLogoImg: data?.moduleId?.chatLogoImg || '',
                    agentName: data?.moduleId?.agentName || '',
                    isAddRotatingAvatarAndLogo: data?.moduleId?.isAddRotatingAvatarAndLogo || false,
                });
                setIsChatHistoryLoading(false)
            })
            .catch(() => showSnackbar().failure('Something went wrong'))
            .finally(() => {
                setIsLoading(false);
            });
    }



    /**
   * sets the form data 
   * @param {string} name the name of the field
   * @param {string} value  the value of the field
   */
    const setFormValue = (name, value) => {
        setFormData((prv) => ({ ...prv, [name]: value }))

    }

    const postChatNotes = () => {
        if (chatNotes) {
            const apiCaller = new ApiCaller('ai');
            apiCaller.customPostData(`/modules/conversation/${id}`, { chatNotes })
                .then((data) => {
                    if (data.success) {
                        showSnackbar().success(`${translateThis('Notes added successfully')}`);
                    } else {
                        showSnackbar().failure(data.message);
                    }
                });
        }

    };

    // Update ticket stage (manual)
    const updateStage = (newStage) => {
        const apiCaller = new ApiCaller('modules/conversations');
        apiCaller.customPatchData(`${id}/stage`, { stage: newStage })
            .then((data) => {
                if (data.success) {
                    setSalesData(prev => ({ ...prev, stage: newStage }));
                    showSnackbar().success(`${translateThis('Stage updated to')} ${newStage}`);
                } else {
                    showSnackbar().failure(data.message);
                }
            })
            .catch(() => showSnackbar().failure('Failed to update stage'));
    };

    // Qualification status color helper
    const getQualificationColor = (status) => {
        switch (status) {
            case 'Hot': return 'red';
            case 'Warm': return 'orange';
            case 'Cold': return 'blue';
            default: return 'default';
        }
    };

    // Stage color helper
    const getStageColor = (stage) => {
        switch (stage) {
            case 'Scheduled': return 'cyan';
            case 'Closed Won': return 'green';
            case 'Closed Lost': return 'red';
            case 'New': return 'default';
            default: return 'default';
        }
    };

    const [avatarToggle, setAvatarToggle] = useState(isHumanChatUrl ? 'human-avatar' : 'ai-avatar');
    const [isInitiatingChat, setIsInitiatingChat] = useState(false);
    const [fontSize, setFontSize] = useState(14);
    // Keep avatarToggle in sync with showHumanPanel (e.g. after formData loads or panel switches)
    useEffect(() => {
        setAvatarToggle(showHumanPanel ? 'human-avatar' : 'ai-avatar');
    }, [showHumanPanel]);

    const handleAvatarToggle = async (value) => {
        setAvatarToggle(value);
        if (value === 'ai-avatar') {
            setShowHumanPanel(false);
            return;
        }
        // switching to human — if we already have a ticket, just show the panel
        if (humanTicketId) {
            setShowHumanPanel(true);
            return;
        }
        if (!aiTicketContextId) {
            showSnackbar().failure('No active session found for this ticket');
            setAvatarToggle('ai-avatar');
            return;
        }
        try {
            setIsInitiatingChat(true);
            const apiCaller = new ApiCaller('inbox/agent-initiate');
            const result = await apiCaller.postData({
                contextId: aiTicketContextId,
                moduleId: aiTicketModuleId || null,
                userName: formData.userName || 'Guest',
                email: formData.email || '',
            });
            if (result?.success && result?.data?.ticketId) {
                const newTicketId = String(result.data.ticketId);
                setHumanTicketId(newTicketId);
                setShowHumanPanel(true);
                await fetchHumanTicket(newTicketId, true);
            } else {
                showSnackbar().failure(result?.message || 'Could not initiate chat');
                setAvatarToggle('ai-avatar');
            }
        } catch {
            showSnackbar().failure('Could not initiate chat');
            setAvatarToggle('ai-avatar');
        } finally {
            setIsInitiatingChat(false);
        }
    };

    const headerImages = {
        header1: AppIcons.chatHead1,
        header2: AppIcons.chatHead2,
        header3: AppIcons.chatHead3,
        header4: AppIcons.chatHead4,
        header5: AppIcons.chatHead5,
        header6: AppIcons.chatHead6,
        header7: AppIcons.chatHead7,
    };

    return (
        <>


            <PageLayout
                pageName={'view ticket'}
                pageTitle={'View Ticket'}
                bodyClassName={`module-main-wrp`}
                key={`${appState?.selectedLang}`}
            >


                <div className='main-content-wrapper AI-module-view f-wrp'>

                    <br />
                    <br />


                    <div className='appForm-layout' style={{ zoom: fontSize / 14 }}>


                        {!isLoading ? <Form
                            name="moduleView"
                            // disabled={!isEditEnabled}
                            initialValues={formData}
                            // onFinish={onFinish}
                            // onFinishFailed={onFinishFailed}
                            layout="vertical"
                        >
                            <div className="container">
                                <Row gutter={20}>

                                    <Col span={24}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Ticket details')}</Title>
                                            <div style={{ display: 'flex', gap: 4, zoom: 14 / fontSize, color: 'var(--white)' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setFontSize(f => Math.max(11, f - 1))}
                                                    style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: '11px', fontWeight: 700, letterSpacing: 0.5 }}
                                                >A-</button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFontSize(14)}
                                                    style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: '13px', fontWeight: 700, letterSpacing: 0.5 }}
                                                >A</button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFontSize(f => Math.min(20, f + 1))}
                                                    style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: '15px', fontWeight: 700, letterSpacing: 0.5 }}
                                                >A+</button>
                                            </div>
                                        </div>
                                        <Paragraph style={{ margin: '0' }}>&nbsp;</Paragraph>
                                        <Paragraph style={{ margin: '0' }}>&nbsp;</Paragraph>

                                    </Col>

                                    <Col span={24}>
                                        <div className='ticket-grid'>

                                            {/* ── Main column ── */}
                                            <div className='tg-main-col'>


                                                {/* Conversation */}
                                                {formData.chatTitle && (
                                                    <div className='tg-card'>
                                                        <div className='tg-section-label'>{translateThis('Conversation')}</div>
                                                        <div className='tg-labeled-block'>
                                                            <div className='tg-block-label'>{translateThis('Topic')}</div>
                                                            <div className='tg-block-text'>
                                                                <TypingEffect markdown={formData.chatTitle} />
                                                            </div>
                                                        </div>
                                                        <div className='tg-field-divider' />
                                                        <div className='tg-labeled-block'>
                                                            <div className='tg-block-label'>{translateThis('Summary')}</div>
                                                            <div className='tg-block-text'>
                                                                <TypingEffect markdown={formData.chatSummary} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sales Qualification */}
                                                {formData.agentType === 'sales' && (
                                                    <div className='tg-card'>
                                                        <div className='tg-section-label'>{translateThis('Sales Qualification')}</div>
                                                        <div className='tg-sales-fields'>
                                                            {salesData.interestIn && (
                                                                <div className='tg-field-row'>
                                                                    <span className='tg-field-label'>{translateThis('Interest In')}&nbsp; &nbsp;:</span>
                                                                    <span className='tg-field-value'>{salesData.interestIn}</span>
                                                                </div>
                                                            )}
                                                            {salesData.goal && (
                                                                <div className='tg-field-row'>
                                                                    <span className='tg-field-label'>{translateThis('Goal')}&nbsp; &nbsp;:</span>
                                                                    <span className='tg-field-value'>{salesData.goal}</span>
                                                                </div>
                                                            )}
                                                            {(salesData.timelineUrgency || salesData.qualificationStatus) && (
                                                                <div className='tg-field-row'>
                                                                    {salesData.timelineUrgency && <>
                                                                        <span className='tg-field-label'>{translateThis('Timeline / Urgency')}&nbsp; &nbsp;:</span>
                                                                        <span className='tg-field-value' style={{ marginRight: 20 }}>
                                                                            <Tag>{salesData.timelineUrgency}</Tag>
                                                                        </span>
                                                                    </>}
                                                                    {salesData.qualificationStatus && <>
                                                                        <span className='tg-field-label'>{translateThis('Qualification Status')}&nbsp; &nbsp;:</span>
                                                                        <span className='tg-field-value'>
                                                                            <Tag color={getQualificationColor(salesData.qualificationStatus)}>
                                                                                {salesData.qualificationStatus}
                                                                            </Tag>
                                                                        </span>
                                                                    </>}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className='tg-field-divider' />
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                            <span className='tg-field-label'>{translateThis('Stage')}&nbsp; &nbsp;:</span>
                                                            <Tag color={getStageColor(salesData.stage)} style={{ fontSize: '13px', padding: '3px 10px' }}>
                                                                {salesData.stage || 'New'}
                                                            </Tag>
                                                            {salesData.stage !== 'Scheduled' && (
                                                                <Select
                                                                    value={salesData.stage || 'New'}
                                                                    style={{ width: 150 }}
                                                                    onChange={(value) => updateStage(value)}
                                                                    options={[
                                                                        { value: 'New', label: 'New' },
                                                                        { value: 'Closed Won', label: 'Closed Won' },
                                                                        { value: 'Closed Lost', label: 'Closed Lost' },
                                                                    ]}
                                                                />
                                                            )}
                                                            {salesData.stage === 'Scheduled' && (
                                                                <span style={{ color: '#888', fontStyle: 'italic', fontSize: '12px' }}>
                                                                    ({translateThis('Set automatically via calendar webhook')})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Notes */}
                                                {/* <div className='tg-card'>
                                                    <div className='tg-section-label'>{translateThis('Notes')}</div>

                                                    <Input.TextArea
                                                        value={chatNotes}
                                                        onChange={(e) => setChatNotes(e.target.value)}
                                                        rows={3}
                                                        style={{ resize: 'none' }}
                                                        placeholder={translateThis('Add a note about this ticket...')}
                                                    />
                                                    <Button
                                                        type='primary'
                                                        disabled={!chatNotes}
                                                        onClick={() => postChatNotes()}
                                                        style={{ width: '100%', marginTop: 10 }}
                                                    >
                                                        {translateThis('Add note')}
                                                    </Button>
                                                </div> */}

                                            </div>

                                            {/* ── Side column ── */}
                                            <div className='tg-side-col'>

                                                {/* Customer */}
                                                <div className='tg-meta-card'>
                                                    <div className='tg-meta-title'>{translateThis('Customer')}</div>
                                                    <div className='tg-meta-item'>
                                                        <span className='tg-meta-key'>{translateThis('User name')}</span>
                                                        <span className='tg-meta-val'>{formData.userName || '-'}</span>
                                                    </div>
                                                    <div className='tg-meta-item'>
                                                        <span className='tg-meta-key'>{translateThis('Email')}</span>
                                                        <span className='tg-meta-val'>{formData.email || '-'}</span>
                                                    </div>
                                                    {formData.phoneNumber && (
                                                        <div className='tg-meta-item'>
                                                            <span className='tg-meta-key'>{translateThis('Phone')}</span>
                                                            <span className='tg-meta-val'>{formData.phoneNumber}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Ticket status */}
                                                <div className='tg-meta-card'>
                                                    <div className='tg-meta-title'>{translateThis('Ticket status')}</div>
                                                    {formData.topicStatus && (
                                                        <div style={{ marginBottom: 10 }}>
                                                            <span className='tg-state-badge'>
                                                                <span className='tg-state-dot' />
                                                                {formData.topicStatus}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {humanRequestStatus && (
                                                        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: humanRequestStatus === 'active' ? 'rgba(82, 196, 26, 0.12)' : 'rgba(250, 173, 20, 0.12)', border: `1px solid ${humanRequestStatus === 'active' ? '#52c41a' : '#faad14'}`, borderRadius: 6 }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={humanRequestStatus === 'active' ? '#52c41a' : '#faad14'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                                                                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                                                            </svg>
                                                            <span style={{ fontSize: 12, color: humanRequestStatus === 'active' ? '#52c41a' : '#faad14', fontWeight: 500 }}>
                                                                {humanRequestStatus === 'active' ? translateThis('Human support active') : translateThis('Human support requested')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {formData.ticketNumber && (
                                                        <div className='tg-meta-item'>
                                                            <span className='tg-meta-key'>{translateThis('Ticket ID')}</span>
                                                            <span className='tg-meta-val'>#{formData.ticketNumber}</span>
                                                        </div>
                                                    )}

                                                    {formData.createdAt && (
                                                        <div className='tg-meta-item'>
                                                            <span className='tg-meta-key'>{translateThis('Created')}</span>
                                                            <span className='tg-meta-val'> {dayjs(formData?.createdAt).format(DATE_FORMAT_YEAR_TIME_SHORT)}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Agent */}
                                                <div className='tg-meta-card'>
                                                    <div className='tg-meta-title'>{translateThis('Agent')}</div>
                                                    <div className='tg-meta-item'>
                                                        <span className='tg-meta-key'>{translateThis('Module')}</span>
                                                        <span className='tg-meta-val'>
                                                            <span className='tg-agent-chip'>{formData.moduleTitle || '-'}</span>
                                                        </span>
                                                    </div>
                                                    <div className='tg-meta-item'>
                                                        <span className='tg-meta-key'>{translateThis('Type')}</span>
                                                        <span className='tg-meta-val'>
                                                            {formData.agentType === 'sales' ? translateThis('Sales Agent') : translateThis('AI Agent')}
                                                        </span>
                                                    </div>
                                                </div>

                                            </div>


                                        </div>

                                        <div className='tg-main-col' style={{ marginTop: '14px' }}>
                                            <div className='tg-card'>
                                                <div className='tg-section-label'>{translateThis('Notes')}</div>
                                                <Form.Item
                                                    // label={translateThis('Notes')}
                                                    name="chatNotes"
                                                    readOnly
                                                    style={{ marginBottom: '30px' }}
                                                >
                                                    <Input.TextArea value={chatNotes} onChange={(e) => setChatNotes(e.target.value)} rows={3} style={{ resize: 'none' }} placeholder={translateThis('Type here')} />
                                                </Form.Item>
                                                <Button type="primary" disabled={!chatNotes}
                                                    onClick={() => postChatNotes()}
                                                    // icon={<SVGIcons.UploadFileIcon />}
                                                    className='upload-file-btn'
                                                >
                                                    {translateThis("Add Note")}
                                                </Button>

                                            </div>
                                        </div>
                                    </Col>


                                    {/* old style */}

                                    {/* <Col span={24} xs={24} sm={24} md={24} lg={24} xl={24}>
                                        <Flex wrap="wrap" gap={'5%'} vertical={false}>
                                            <div className='view-card'>
                                                <span>{translateThis('User name')}&nbsp; &nbsp;:</span>
                                                <p>{formData?.userName || '-'}</p>
                                            </div>
                                            <div className='view-card'>
                                                <span>{translateThis('User email')}&nbsp;&nbsp;:</span>
                                                <p>{formData?.email || '-'}</p>
                                            </div>
                                            <div className='view-card'>
                                                <span>{translateThis('Module title')}&nbsp;&nbsp;:</span>
                                                <p>{formData?.moduleTitle || '-'}</p>
                                            </div>
                                            {formData.topicStatus ? <div className='view-card'>
                                                <span>{translateThis('State')}&nbsp;&nbsp;:</span>
                                                <p>{formData?.topicStatus || '-'}</p>
                                            </div> : null}
                                        </Flex>
                                    </Col> */}

                                    {/* -------- Sales Qualification Data -------- */}
                                    {/* {formData.agentType === 'sales' && (
                                        <Col span={24} style={{ marginBottom: '20px' }}>
                                            <div style={{
                                                background: 'linear-gradient(135deg, rgba(117, 23, 248, 0.05) 0%, rgba(196, 56, 239, 0.05) 100%)',
                                                border: '1px solid rgba(117, 23, 248, 0.2)',
                                                borderRadius: '12px',
                                                padding: '20px',
                                            }}>
                                                <Title level={5} style={{ margin: '0 0 16px 0' }}>{translateThis('Sales Qualification')}</Title>
                                                <Flex wrap="wrap" gap={'5%'} vertical={false}>
                                                    {salesData.interestIn && (
                                                        <div className='view-card'>
                                                            <span>{translateThis('Interest In')}&nbsp;&nbsp;:</span>
                                                            <p>{salesData.interestIn}</p>
                                                        </div>
                                                    )}
                                                    {salesData.goal && (
                                                        <div className='view-card'>
                                                            <span>{translateThis('Goal')}&nbsp;&nbsp;:</span>
                                                            <p>{salesData.goal}</p>
                                                        </div>
                                                    )}
                                                    {salesData.timelineUrgency && (
                                                        <div className='view-card'>
                                                            <span>{translateThis('Timeline / Urgency')}&nbsp;&nbsp;:</span>
                                                            <p><Tag style={{ color: '#000' }}>{salesData.timelineUrgency}</Tag></p>
                                                        </div>
                                                    )}
                                                    {salesData.qualificationStatus && (
                                                        <div className='view-card'>
                                                            <span>{translateThis('Qualification Status')}&nbsp;&nbsp;:</span>
                                                            <p><Tag color={getQualificationColor(salesData.qualificationStatus)}>{salesData.qualificationStatus}</Tag></p>
                                                        </div>
                                                    )}
                                                </Flex>
                                                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ fontWeight: 500, color: "#fff" }}>{translateThis('Stage')}:</span>
                                                    <Tag color={getStageColor(salesData.stage)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                                                        {salesData.stage || 'New'}
                                                    </Tag>
                                                    {salesData.stage !== 'Scheduled' && (
                                                        <Select
                                                            value={salesData.stage || 'New'}
                                                            style={{ width: 160 }}
                                                            onChange={(value) => updateStage(value)}
                                                            options={[
                                                                { value: 'New', label: 'New' },
                                                                { value: 'Closed Won', label: 'Closed Won' },
                                                                { value: 'Closed Lost', label: 'Closed Lost' },
                                                            ]}
                                                        />
                                                    )}
                                                    {salesData.stage === 'Scheduled' && (
                                                        <span style={{ color: '#888', fontStyle: 'italic', fontSize: '12px' }}>
                                                            ({translateThis('Set automatically via calendar webhook')})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    <Col span={24} xs={24} sm={24} md={24} lg={24} xl={24}>
                                        <Flex wrap="wrap" gap={'5%'} vertical={false}>
                                            <div className='view-card'>
                                                <span>{translateThis('Topic')}&nbsp;&nbsp;:</span>
                                                <TypingEffect markdown={formData.chatTitle} />
                                            </div>
                                        </Flex>
                                    </Col> */}

                                    {/* <Col span={12} xs={24} sm={24} md={8} lg={8} xl={6}>

                                        <Form.Item
                                            label={translateThis('User name')}
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input
                                                value={formData.userName}
                                                readOnly
                                                placeholder={translateThis('Type here')}
                                                className="custom-textfield"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={8} lg={8} xl={6}>

                                        <Form.Item
                                            label={translateThis('User email')}
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input
                                                value={formData.email}
                                                readOnly
                                                placeholder={translateThis('Type here')}
                                                className="custom-textfield"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={8} lg={8} xl={6}>

                                        <Form.Item
                                            label={translateThis('Module title')}
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input
                                                value={formData.moduleTitle}
                                                readOnly
                                                placeholder={translateThis('Type here')}
                                                className="custom-textfield"
                                            />
                                        </Form.Item>
                                    </Col>

                                    {formData.topicStatus ? <Col span={12} xs={24} sm={24} md={8} lg={8} xl={6}>

                                        <Form.Item
                                            label={translateThis('State')}
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input
                                                value={formData.topicStatus}
                                                readOnly
                                                placeholder={translateThis('Type here')}
                                                className="custom-textfield"
                                            />
                                        </Form.Item>


                                    </Col> : null} */}

                                    {/* <Col span={24}>
                                        <Title level={4} className='revamp-title' style={{ margin: '0' }}>{translateThis('Topic')}</Title>
                                       
                                        <div className='chat-window chatSummery f-wrp'>
                                            <TypingEffect markdown={formData.chatTitle} />
                                        </div> */}
                                    {/* <Form.Item
                                            label={translateThis('Topic')}
                                            name="chatTitle"
                                            readOnly
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input.TextArea value={formData.chatTitle} readOnly rows={2} style={{ resize: 'none' }} placeholder={translateThis('Type here')} />
                                        </Form.Item> */}
                                    {/* </Col> */}

                                    {/* {formData?.chatTitle ? <Col span={24}>
                                        <Title level={4} className='revamp-title' style={{ margin: '0' }}>{translateThis('Chat Summary')}</Title>
                                        <div className='chat-window chatSummery f-wrp'>
                                            <TypingEffect markdown={formData?.chatSummary} />
                                        </div>
                                    </Col> : null} */}

                                    {/* <Col span={24}>
                                        <br />
                                        <Title level={4} className='revamp-title' style={{ margin: '0' }}>{translateThis('Notes')}</Title>
                                        <Paragraph style={{ margin: '0' }}>&nbsp;</Paragraph>
                                        <Form.Item
                                            // label={translateThis('Notes')}
                                            name="chatNotes"
                                            readOnly
                                            style={{ marginBottom: '30px' }}
                                        >
                                            <Input.TextArea value={chatNotes} onChange={(e) => setChatNotes(e.target.value)} rows={3} style={{ resize: 'none' }} placeholder={translateThis('Type here')} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Button type="primary" disabled={!chatNotes}
                                            onClick={() => postChatNotes()}
                                            // icon={<SVGIcons.UploadFileIcon />}
                                            className='upload-file-btn'
                                        >
                                            {translateThis("Add Note")}
                                        </Button>
                                    </Col> */}

                                    <Col span={24}>
                                        <br />
                                        <br />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Title level={4} className='revamp-title tg-section-label' style={{ margin: '0' }}>
                                                {showHumanPanel ? translateThis('Conversation') : translateThis('Chat history')}
                                            </Title>
                                            {showHumanPanel && <>
                                                {chatStatus === 'pending' && <Tag color='orange'>Waiting for agent</Tag>}
                                                {chatStatus === 'active' && <Tag color='green'>Active</Tag>}
                                                {chatStatus === 'completed' && <Tag color='default'>Completed</Tag>}
                                                {chatStatus === 'timed_out' && <Tag color='red'>Timed Out</Tag>}
                                            </>}
                                        </div>
                                        {!showHumanPanel && humanRequestStatus && (
                                            <div style={{ marginTop: 10, padding: '10px 14px', background: humanRequestStatus === 'active' ? 'rgba(82, 196, 26, 0.1)' : 'rgba(250, 173, 20, 0.1)', border: `1px solid ${humanRequestStatus === 'active' ? '#52c41a' : '#faad14'}`, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={humanRequestStatus === 'active' ? '#52c41a' : '#faad14'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                                                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                                                </svg>
                                                <span style={{ flex: 1, fontSize: 13, color: humanRequestStatus === 'active' ? '#52c41a' : '#faad14', fontWeight: 500 }}>
                                                    {humanRequestStatus === 'active'
                                                        ? translateThis('Human support chat is active')
                                                        : translateThis('User has requested human support')}
                                                </span>
                                                <Button
                                                    size="small"
                                                    className="viewHumanChat-btn"
                                                    style={{ borderColor: humanRequestStatus === 'active' ? 'var(--backgroundBG)' : 'var(--backgroundBG)', color: humanRequestStatus === 'active' ? 'var(--white)' : 'var(--white)' }}
                                                    onClick={async () => {
                                                        if (humanRequestTicketId) {
                                                            // Load the user-initiated human ticket inline
                                                            setHumanTicketId(humanRequestTicketId);
                                                            setShowHumanPanel(true);
                                                            await fetchHumanTicket(humanRequestTicketId, true);
                                                        } else {
                                                            handleAvatarToggle('human-avatar');
                                                        }
                                                    }}
                                                    loading={isInitiatingChat}
                                                >
                                                    {translateThis('View human chat')}
                                                </Button>
                                            </div>
                                        )}
                                        <Paragraph style={{ margin: '0' }}>&nbsp;</Paragraph>

                                        <div className='chat-flex-box'>
                                            <div className='avatar-Toggle-box'>

                                                <Form.Item
                                                    className='avatarToggle'
                                                    // name="role"
                                                    style={{ marginBottom: '15px' }}
                                                >
                                                    <Radio.Group
                                                        value={avatarToggle}
                                                        onChange={(e) => handleAvatarToggle(e.target.value)}
                                                        disabled={isInitiatingChat}
                                                    >
                                                        <Radio value="ai-avatar">
                                                            <div className='each-avtr-box'>
                                                                {formData.avatarImg ? (
                                                                    <AvatarViewer className='chatbotIcon' avatar={formData.avatarImg} />
                                                                ) : (
                                                                    <img className='chatbotIcon' src={AppIcons.chatBot} alt='chat bot' />
                                                                )}
                                                                <p>{translateThis('AI')}</p>
                                                            </div>
                                                        </Radio>
                                                        <Radio value="human-avatar">
                                                            <div className='each-avtr-box'>
                                                                <img className='chatbotIcon' src={AppIcons.avatar1} alt='chat bot' />
                                                                <p>{translateThis('Human')}</p>
                                                            </div>
                                                        </Radio>
                                                    </Radio.Group>
                                                </Form.Item>

                                                {/* ── Accept / End / Restart Chat buttons (human mode only) ── */}
                                                {showHumanPanel && (chatStatus === 'pending' || chatStatus === 'active') && (
                                                    <div className='chat-response-btns' style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                                                        {chatStatus === 'pending' && (
                                                            <Button className='accept-chat' type='primary' onClick={acceptChat}>{translateThis('Accept Chat')}</Button>
                                                        )}
                                                        {chatStatus === 'active' && (
                                                            <Button className='end-chat' danger onClick={endChat}>{translateThis('End Chat')}</Button>
                                                        )}
                                                    </div>
                                                )}
                                                {showHumanPanel && (chatStatus === 'completed' || chatStatus === 'timed_out') && (
                                                    <div className='chat-response-btns' style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                                                        <Button type='primary' onClick={restartChat} loading={isInitiatingChat}>
                                                            {translateThis('Restart Chat')}
                                                        </Button>
                                                    </div>
                                                )}


                                            </div>

                                            <div className={`chat-theme-wrapper ${moduleTheme.themeBackgroundColor}`}>
                                                <div className={`chat-theme-header ${moduleTheme.isHeaderPresent ? 'header-added' : 'header-removed'}`}>
                                                    {moduleTheme.isHeaderPresent && (
                                                        <img className='chat-head' src={headerImages[moduleTheme.chatHeaderImg] || headerImages.header1} alt='' />
                                                    )}
                                                    <div className='chat-theme-title'>
                                                        {moduleTheme.chatLogoImg && (
                                                            <LogoPreview
                                                                uploadedLogo={null}
                                                                formData={{
                                                                    chatLogoImg: moduleTheme.chatLogoImg,
                                                                    isAddRotatingAvatarAndLogo: moduleTheme.isAddRotatingAvatarAndLogo,
                                                                    avatarImg: formData.avatarImg,
                                                                }}
                                                            />
                                                        )}
                                                        <span>{moduleTheme.conversationBoxTitle || moduleTheme.agentName || formData.moduleTitle}</span>
                                                    </div>
                                                </div>
                                            <div className='maxWidth985px'>
                                                {/* ── Conversation window (always visible) ── */}
                                                <div
                                                    ref={liveChatRef}
                                                    className={`chat-window live-chat-window ${showHumanPanel && chatStatus === 'active' && 'show-input'}`}
                                                    style={{ height: `${(showHumanPanel && chatStatus === 'active') ? 'calc(100dvh - 230px)' : 'calc(100dvh - 175px)'}`,
                                                        maxHeight: 500,
                                                        overflowY: 'auto', padding: 16 }}
                                                >
                                                    {/* AI conversation */}
                                                    {(showHumanPanel ? aiHistory : questions).map((q, i) => (
                                                        <React.Fragment key={`ai-${i}`}>
                                                            {/* Customer question → LEFT with placeholder avatar */}
                                                            <div className='tg-bubble-agent'>
                                                                <div className='tg-bubble-avatar'>
                                                                    <img className='chatbotIcon user-placeholder-icon' src={AppIcons.userPlaceholder} alt='customer' />
                                                                </div>
                                                                <div className='tg-bubble-msg'>
                                                                    <span>{q.question}</span>
                                                                </div>
                                                            </div>
                                                            {/* AI response → RIGHT */}
                                                            <div className='tg-bubble-user'>
                                                                <div className='tg-bubble-msg formatted-text'>
                                                                    <TypingEffect markdown={q.answer?.replace(/<human-support>/g, '').replace(/<contact-info[^/]*\/>/g, '').trim()} />
                                                                </div>
                                                            </div>
                                                        </React.Fragment>
                                                    ))}

                                                    {/* Handoff divider */}
                                                    {liveMessages.length > 0 && (showHumanPanel ? aiHistory : questions).length > 0 && (
                                                        <div className='chat-handoff-divider'>
                                                            <span>{translateThis('Handoff to human agent')}</span>
                                                        </div>
                                                    )}

                                                    {/* Human agent messages */}
                                                    {(() => {
                                                        const renderItems = [];
                                                        let lastDateLabel = null;
                                                        liveMessages.forEach((msg, index) => {
                                                            if (msg.timestamp) {
                                                                const label = getDateLabel(msg.timestamp);
                                                                if (label !== lastDateLabel) {
                                                                    lastDateLabel = label;
                                                                    renderItems.push({ type: 'date', label, key: `date-${index}` });
                                                                }
                                                            }
                                                            renderItems.push({ type: 'message', msg, index });
                                                        });
                                                        return renderItems.map((item) => {
                                                            if (item.type === 'date') {
                                                                return (
                                                                    <div key={item.key} className='chat-date-separator'>
                                                                        <span>{item.label}</span>
                                                                    </div>
                                                                );
                                                            }
                                                            const { msg, index } = item;
                                                            const ts = msg.timestamp ? dayjs(msg.timestamp).format('h:mm A') : '';
                                                            return msg.sender === 'agent' ? (
                                                                <div key={index} className='tg-bubble-user'>
                                                                    <span>{msg.message}</span>
                                                                    {ts && <small className='tg-msg-time'>{ts}</small>}
                                                                </div>
                                                            ) : (
                                                                <div key={index} className='tg-bubble-agent'>
                                                                    <div className='tg-bubble-avatar'>
                                                                        <img className='chatbotIcon user-placeholder-icon' src={AppIcons.userPlaceholder} alt='customer' />
                                                                    </div>
                                                                    <div className='tg-bubble-msg'>
                                                                        <span>{msg.message}</span>
                                                                        {ts && <small className='tg-msg-time'>{ts}</small>}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    })()}

                                                    {/* Empty state */}
                                                    {(showHumanPanel ? aiHistory : questions).length === 0 && liveMessages.length === 0 && (
                                                        <p className='chat-empty-state'>
                                                            {translateThis('No conversation yet')}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* ── Agent input bar (human mode only) ── */}
                                                {showHumanPanel && chatStatus === 'active' && (
                                                    <div className='chat-input module-chat' style={{ marginTop: 8 }}>
                                                        <Input.TextArea
                                                            autoSize={{ minRows: 1, maxRows: 4 }}
                                                            value={agentInput}
                                                            onChange={(e) => setAgentInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                    e.preventDefault();
                                                                    sendAgentMessage();
                                                                }
                                                            }}
                                                            style={{ resize: 'none' }}
                                                            placeholder={translateThis('Type a message...')}
                                                        />
                                                        <Button
                                                            type='primary'
                                                            className='sent-btn'
                                                            icon={<SVGIcons.SentIcon />}
                                                            onClick={sendAgentMessage}
                                                        />
                                                    </div>
                                                )}

                                            </div>
                                            </div>
                                        </div>



                                        <br />

                                    </Col>

                                </Row>

                            </div>

                        </Form> : <span>{translateThis('Loading..')}</span>}
                    </div>
                </div>
            </PageLayout >














        </>
    );
}
